import React, { createContext, useContext, useMemo } from "react"

type Insets = { top: number; bottom: number; left: number; right: number }

const defaultInsets: Insets = { top: 0, bottom: 0, left: 0, right: 0 }

const SafeAreaContext = createContext(defaultInsets)

export function SafeAreaProvider({
  children,
  style,
}: {
  children: React.ReactNode
  style?: React.CSSProperties
}) {
  const insets = useMemo<Insets>(() => {
    if (typeof window === "undefined") return defaultInsets
    const probe = document.createElement("div")
    probe.style.cssText =
      "position:fixed;top:0;left:0;padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);"
    document.body.appendChild(probe)
    const styles = getComputedStyle(probe)
    const value = {
      top: parseFloat(styles.paddingTop) || 0,
      right: parseFloat(styles.paddingRight) || 0,
      bottom: parseFloat(styles.paddingBottom) || 0,
      left: parseFloat(styles.paddingLeft) || 0,
    }
    document.body.removeChild(probe)
    return value
  }, [])

  return (
    <SafeAreaContext.Provider value={insets}>
      <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, ...style }}>
        {children}
      </div>
    </SafeAreaContext.Provider>
  )
}

export function useSafeAreaInsets() {
  return useContext(SafeAreaContext)
}

export function useSafeAreaFrame() {
  const insets = useSafeAreaInsets()
  return {
    x: 0,
    y: 0,
    width: typeof window !== "undefined" ? window.innerWidth : 375,
    height: typeof window !== "undefined" ? window.innerHeight : 812,
    ...insets,
  }
}

export const initialWindowMetrics = {
  frame: { x: 0, y: 0, width: 0, height: 0 },
  insets: defaultInsets,
}

export { SafeAreaView } from "./primitives"
